/* eslint-disable @typescript-eslint/member-ordering */
import murmurhash from 'murmurhash';
import { Clock } from './clock.service';

export class Timestamp {
  protected _millis: number;
  protected _counter: number;
  protected _node: string;

  static config = {
    /** Maximum physical clock drift allowed, in ms */
    maxDrift: 120000,
  };

  constructor(millis: number, counter: number, node: string) {
    this._millis = millis;
    this._counter = counter;
    this._node = node;
  }

  valueOf() {
    return this.toString();
  }

  toString() {
    return [
      new Date(this.millis()).toISOString(),
      ('0000' + this.counter().toString(16).toUpperCase()).slice(-4),
      ('0000000000000000' + this.node()).slice(-16),
    ].join('-');
  }

  millis() {
    return this._millis;
  }

  counter() {
    return this._counter;
  }

  node() {
    return this._node;
  }

  hash() {
    return murmurhash.v3(this.toString());
  }

  /**
   * Timestamp generator initialization
   * * sets the node ID to an arbitrary value
   * * useful for mocking/unit testing
   * @param options
   */
  static init(options: { maxDrift: number }) {
    if (options.maxDrift) {
      this.config.maxDrift = options.maxDrift;
    }
  }

  /**
   * Timestamp send. Generates a unique, monotonic timestamp suitable
   * for transmission to another system in string format
   */
  static send(clock: Clock) {
    // Retrieve the local wall time
    const phys = Date.now();

    // Unpack the clock.timestamp logical time and counter
    const lOld = clock.timestamp.millis();
    const cOld = clock.timestamp.counter();

    // Calculate the next logical time and counter
    // * ensure that the logical time never goes backward
    // * increment the counter if phys time does not advance
    const lNew = Math.max(lOld, phys);
    const cNew = lOld === lNew ? cOld + 1 : 0;

    // Check the result for drift and counter overflow
    if (lNew - phys > Timestamp.config.maxDrift) {
      throw new ClockDriftError(lNew, phys, Timestamp.config.maxDrift);
    }
    if (cNew > 65535) {
      throw new OverflowError();
    }

    // Repack the logical time/counter
    clock.timestamp.setMillis(lNew);
    clock.timestamp.setCounter(cNew);

    return new Timestamp(
      clock.timestamp.millis(),
      clock.timestamp.counter(),
      clock.timestamp.node()
    );
  }

  /**
   * Timestamp receive. Parses and merges a timestamp from a remote
   * system with the local timeglobal uniqueness and monotonicity are
   * preserved
   */
  static recv(clock: Clock, msg: Timestamp, fromWorker = false) {
    const phys = Date.now();

    // Unpack the message wall time/counter
    const lMsg = msg.millis();
    const cMsg = msg.counter();

    // Assert the node id and remote clock drift
    // if (msg.node() === clock.timestamp.node() && !fromWorker) {
    //   throw new DuplicateNodeError(clock.timestamp.node());
    // }
    if (lMsg - phys > Timestamp.config.maxDrift) {
      throw new ClockDriftError();
    }

    // Unpack the clock.timestamp logical time and counter
    const lOld = clock.timestamp.millis();
    const cOld = clock.timestamp.counter();

    // Calculate the next logical time and counter.
    // Ensure that the logical time never goes backward;
    // * if all logical clocks are equal, increment the max counter,
    // * if max = old > message, increment local counter,
    // * if max = messsage > old, increment message counter,
    // * otherwise, clocks are monotonic, reset counter
    const lNew = Math.max(Math.max(lOld, phys), lMsg);
    const cNew =
      lNew === lOld && lNew === lMsg
        ? Math.max(cOld, cMsg) + 1
        : lNew === lOld
        ? cOld + 1
        : lNew === lMsg
        ? cMsg + 1
        : 0;

    // Check the result for drift and counter overflow
    if (lNew - phys > Timestamp.config.maxDrift) {
      throw new ClockDriftError();
    }
    if (cNew > 65535) {
      throw new OverflowError();
    }

    // Repack the logical time/counter
    clock.timestamp.setMillis(lNew);
    clock.timestamp.setCounter(cNew);

    return new Timestamp(
      clock.timestamp.millis(),
      clock.timestamp.counter(),
      clock.timestamp.node()
    );
  };

  /**
   * Converts a fixed-length string timestamp to the structured value
   */
  static parse(timestamp: string) {
    const parts = timestamp.split('-');
    if (parts && parts.length === 5) {
    const millis = Date.parse(parts.slice(0, 3).join('-')).valueOf();
    const counter = parseInt(parts[3], 16);
    const node = parts[4];
    if (!isNaN(millis) && !isNaN(counter))
        return new Timestamp(millis, counter, node);
    }
  };

  static since(isoString: string) {
    return isoString + '-0000-0000000000000000';
  };

  static sinceTimestamp(date: number) {
    return (new Date(date)).toISOString() + '-0000-0000000000000000';
  };

  static toDate(timestamp: string) {
    return new Date(timestamp.substring(0, 24));
  }

}

class DuplicateNodeError extends Error {
  constructor(node: string) {
    super();
    this.name = 'DuplicateNodeError';
    this.message = 'duplicate node identifier ' + node;
  }
};

class ClockDriftError extends Error {
  constructor(...args: any[]) {
    super();
    this.name = 'ClockDriftError';
    this.message = ['maximum clock drift exceeded'].concat(args).join(' ');
  }
};

class OverflowError extends Error {
  constructor() {
    super();
    this.name = 'OverflowError';
    this.message = 'timestamp counter overflow';
  }
};

export class MutableTimestamp extends Timestamp {
  setMillis(n: number) {
    this._millis = n;
  }

  setCounter(n: number) {
    this._counter = n;
  }

  setNode(n: string) {
    this._node = n;
  }

  static from(timestamp: Timestamp) {
    return new MutableTimestamp(
      timestamp.millis(),
      timestamp.counter(),
      timestamp.node()
    );
  }
}
